import React, {useState, useEffect, useCallback, useContext, useMemo, useRef} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {Link, useNavigate, useParams} from "react-router-dom";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faSearch,
  faStickyNote,
  faCalendarDay,
  faKeyboard,
  faBoxArchive,
  faArchive,
  faTrash,
  faPaperclip,
  faNoteSticky,
  faEdit,
  faShareFromSquare,
  faMagnifyingGlassArrowRight,
  faRotateLeft,
  faCircleInfo,
  faTriangleExclamation,
  faClone,
  faLock
} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import {
  buildChecksheetDateScrollUrl,
  formatRecordDateSearchQuery,
  hasStructuredDeepLinkSearch,
  isLegacyDateScrollDeepLink,
  parseChecksheetsDeepLink
} from "../../utils/checksheetsDeepLink.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {SettingsContext} from "../../contexts/settings.js";
import {AuthContext} from "../../contexts/auth.js";
import {NavContext} from "../../contexts/nav.js";
import {useToast} from "../../contexts/toast.js";

// Utils
import {prettyDateWithDayInUserTimezone} from "../../utils/helpers.js";
import {isMobile} from "../../utils/responsive.js";

// Components
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";
import ToggleSwitch from "../../components/ToggleSwitch.js";
import Dropdown from "../../components/Dropdown.js";
import ModalRecordView from "./ModalRecordView.js";
import ModalNotes from "./ModalNotes.js";
import ModalAttachments from "./ModalAttachments.js";
import ArchiveRecord from "../general/ArchiveRecord.js";
import Modal from "../../components/Modal.js";
import ModalFieldRestrictions from "./ModalFieldRestrictions.js";
import ModalReviews from "../tasks/ModalReviews.js";
import ModalCloneChecksheet from "./ModalCloneChecksheet.js";
import Badge from "../../components/Badge.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {colors, heroTheme, pad, radius} from "../../style/components/variables.js";
import {
  Abbr,
  Arrow,
  Button,
  ButtonWrapper,
  Error,
  Heading,
  Inline,
  Pill,
  Search,
  SearchIcon,
  SearchWrapper,
  Small,
  TableFooter,
  TableWrapper,
  Text,
  HeadingMedium
} from "../../style/components/general.js";

const TableRecord = ({
  index,
  checksheet,
  recordsRefreshKey = 0,
  publishDraft,
  showExport,
  showDelete,
  archiveChecksheet,
  restoreChecksheet,
  locks,
  handleViewAll,
  queryInfo,
  setQueryInfo,
  refresh,
  triggerOpen,
  onOpen,
  handledOpen
}) => {
  const isMounted = useMountedState();

  const navigate = useNavigate();

  const {slug} = useParams();
  const {allStates} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);
  const {atLeast, roleCanAccessResource, roles, roleCanAccessPage, currentUser} =
    useContext(AuthContext);
  const {facility} = useContext(FacilityNavContext);
  const {addToast} = useToast();

  const targetChecksheet = checksheet;
  const [open, setOpen] = useState(false);
  const [detailChecksheet, setDetailChecksheet] = useState(null);
  const [records, setRecords] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState({});
  const [searchType, setSearchType] = useState("text");
  const [query, setQuery] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [orderBy, setOrderBy] = useState("desc");
  const [groupBy, setGroupBy] = useState(checksheet?.frequency ? "dateDue" : "completedAt");
  const [limit, setLimit] = useState(10);
  const [limits, setLimits] = useState([]);
  // Modals
  const [modalCloneVisible, setModalCloneVisible] = useState(false);
  const [modalNotesVisible, setModalNotesVisible] = useState(false);
  const [modalAttachmentsVisible, setModalAttachmentsVisible] = useState(false);
  const [modalAcknowledgeVisible, setModalAcknowledgeVisible] = useState(false);
  const [modalExternalVisible, setModalExternalVisible] = useState(false);
  const [modalFieldRestrictionsVisible, setModalFieldRestrictionsVisible] = useState(false);
  const [modalReviewsVisible, setModalReviewsVisible] = useState(false);

  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiChecksheetRecords, loading: loadingRecords} = useApi("checksheet-records");
  const {api: apiChecksheetLock} = useApi("checksheet-locks");
  const {api: apiUsers} = useApi("users");

  // Slim /checksheets list omits builder by default; hydrate on expand for notes/view/modals.
  const resolvedChecksheet = detailChecksheet || checksheet;

  useEffect(() => {
    if (!open) return undefined;
    if (checksheet?.builder) {
      setDetailChecksheet(null);
      return undefined;
    }

    let cancelled = false;
    apiChecksheets
      .callGet(checksheet.id, {
        withState: true,
        withCurrentState: true,
        withCounts: true
      })
      .then(({status, data}) => {
        if (!cancelled && status === 200 && data) setDetailChecksheet(data);
      });

    return () => {
      cancelled = true;
    };
  }, [open, checksheet, apiChecksheets]);

  const form = useForm({
    defaultValues: {
      search: ""
    }
  });
  const {reset, register} = form;

  const isActive = useMemo(() => facility && !facility.isDeleted, [facility]);

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return [
      ...Object.entries(allStates).map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }))
    ];
  }, [allStates]);

  const state = useMemo(() => {
    if (!facilityStates || !checksheet?.state || !checksheet?.stateActive) return null;
    return facilityStates.find(({id}) => id === `${checksheet?.state.facilityStateId}`) || null;
  }, [checksheet, facilityStates]);

  const lockKey = `checksheet_${targetChecksheet.id}`;
  const lockInfo = locks?.[lockKey];
  const lockedByOther = Boolean(
    lockInfo?.user && currentUser?.publicId && lockInfo.user !== currentUser.publicId
  );
  const [lockHolder, setLockHolder] = useState(null);
  const [apiLockState, setApiLockState] = useState(null);

  const clearRecordDeepLinkUrl = useCallback(
    (checksheetId, dateStr) => {
      const path = buildChecksheetDateScrollUrl(slug, dateStr, checksheetId);
      if (path) navigate(path, {replace: true});
    },
    [navigate, slug]
  );

  const dateStrForUrlCleanup = useCallback(
    (record, dateOverride) =>
      dateOverride ||
      query ||
      (record ? formatRecordDateSearchQuery(record, settings?.timezone) : undefined),
    [query, settings?.timezone]
  );

  const openRecordFromDeepLink = useCallback(
    (localRecords, recordId) => {
      const match = localRecords?.find(record => record.id === recordId);
      if (!match) return false;

      setSelected(match);
      setVisible(true);
      setInitialLoad(false);
      clearRecordDeepLinkUrl(checksheet.id, dateStrForUrlCleanup(match));
      return true;
    },
    [clearRecordDeepLinkUrl, dateStrForUrlCleanup, checksheet.id]
  );

  const openRecordById = useCallback(
    recordId => {
      if (openRecordFromDeepLink(records, recordId)) return Promise.resolve(true);

      return apiChecksheetRecords
        .callGet(recordId, {withPrevious: true})
        .then(({status, data}) => {
          if (status === 200 && data && data.facilityChecksheetId === checksheet.id) {
            setSelected(data);
            setVisible(true);
            setInitialLoad(false);
            clearRecordDeepLinkUrl(checksheet.id, dateStrForUrlCleanup(data));
            return true;
          }
          return false;
        });
    },
    [
      apiChecksheetRecords,
      clearRecordDeepLinkUrl,
      dateStrForUrlCleanup,
      openRecordFromDeepLink,
      records,
      checksheet.id
    ]
  );

  const loadRecordDetail = useCallback(
    recordId =>
      apiChecksheetRecords.callGet(recordId).then(({status, data}) => {
        if (status === 200 && data) {
          setSelected(data);
          return data;
        }
        addToast("Unable to load this record", "error");
        return null;
      }),
    [apiChecksheetRecords, addToast]
  );

  // Legacy hash-only record ids (checksheets#recordId). Structured and ?date#checksheet use other paths.
  const openModal = useCallback(
    localRecords => {
      const {hash, search} = window.location;
      if (!hash || !initialLoad || hasStructuredDeepLinkSearch(search)) return;

      const parsed = parseChecksheetsDeepLink({search, hash});
      const hashNum = parseInt(hash.replace(/^#c?/, ""), 10);
      if (!hashNum || Number.isNaN(hashNum)) return;

      // ?{date}#{checksheetId} — scroll/filter only (parent queryInfo / applyLegacyDateScrollFromUrl).
      if (isLegacyDateScrollDeepLink(parsed)) {
        setInitialLoad(false);
        return;
      }

      // #{checksheetId} scroll link — hash is the table id, not a record id.
      if (!search && hashNum === checksheet.id) {
        setInitialLoad(false);
        return;
      }

      if (!openRecordFromDeepLink(localRecords, hashNum)) {
        openRecordById(hashNum);
      }
    },
    [initialLoad, openRecordById, openRecordFromDeepLink, checksheet.id]
  );

  const getChecksheetRecords = useCallback(
    p => {
      const payload = {
        facilityChecksheetId: checksheet.id,
        page: p || currentPage || 1,
        orderBy: orderBy,
        groupBy: groupBy,
        limit: limit
      };

      if (query) payload.filter = JSON.stringify({Search: query});

      apiChecksheetRecords.callGet(null, payload).then(({status, data}) => {
        if (status === 200 && data) {
          setPageTotal(data.pages);
          setRecords(data.records);

          // Page Limit
          const {total: dataTotal} = data;
          setTotal(dataTotal);
          let count = 10;
          const temp = [];
          while (count < total + 10 && count <= 30) {
            temp.push(count);
            count += 10;
          }
          setLimits(temp);
          if (initialLoad) openModal(data.records);
        }
      });
    },
    [
      apiChecksheetRecords,
      groupBy,
      initialLoad,
      limit,
      openModal,
      orderBy,
      query,
      checksheet.id,
      total,
      currentPage
    ]
  );

  const handleClone = useCallback(
    ({name}) => {
      const payload = {
        name,
        clone: true
      };

      apiChecksheets.callPatch(checksheet.id, payload).then(({status, data}) => {
        if (status === 200 && data) {
          setModalCloneVisible(false);
          refresh();
        }
      });
    },
    [checksheet, apiChecksheets, refresh]
  );

  const prevRecordsRefreshKeyRef = useRef(recordsRefreshKey);

  useEffect(() => {
    if (recordsRefreshKey > 0 && recordsRefreshKey !== prevRecordsRefreshKeyRef.current && open) {
      getChecksheetRecords();
    }
    prevRecordsRefreshKeyRef.current = recordsRefreshKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsRefreshKey, open]);

  // Open first table
  useEffect(() => {
    if (isMounted() && index === 1 && !handledOpen?.current) {
      if (handledOpen) handledOpen.current = true;
      setOpen(true);
      if (onOpen) onOpen(true, checksheet.id);
    } else if (isMounted() && onOpen) onOpen(open, checksheet.id);
  }, [isMounted, onOpen, index, checksheet, open, handledOpen]);

  useEffect(() => {
    if (isMounted() && triggerOpen !== undefined && triggerOpen !== open) setOpen(triggerOpen);
  }, [isMounted, onOpen, triggerOpen, checksheet, open]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && checksheet.id && !records && open) getChecksheetRecords();
  }, [isMounted, checksheet, getChecksheetRecords, records, open]);

  // Update checksheet records on sort or limit
  useEffect(() => {
    if (isMounted() && records) getChecksheetRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, orderBy, groupBy, limit, query]);

  const handleSearch = e => {
    setCurrentPage(1);
    setQuery(e.target.value);
  };

  // Set lock holder name from API or socket while API lock state is loading
  useEffect(() => {
    if (apiLockState?.user) {
      setLockHolder(`${apiLockState.user.firstName} ${apiLockState.user.lastName}`);
      return undefined;
    }

    if (!isMounted() || !lockedByOther || !lockInfo?.user) {
      setLockHolder(null);
      return undefined;
    }

    let cancelled = false;
    apiUsers.callGet(lockInfo.user).then(response => {
      if (cancelled) return;
      if (response?.status === 200 && response?.data) {
        setLockHolder(`${response.data.firstName} ${response.data.lastName}`);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isMounted, apiLockState?.user, lockedByOther, lockInfo?.user, apiUsers]);

  // API is the source of truth for lock visuals; socket updates trigger a refresh.
  useEffect(() => {
    if (!open) {
      setApiLockState(null);
      return undefined;
    }

    let cancelled = false;
    apiChecksheetLock.callGet(targetChecksheet.id).then(response => {
      if (cancelled) return;
      if (response?.status === 200 && response?.data) {
        setApiLockState(response.data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, targetChecksheet.id, apiChecksheetLock, lockInfo?.user]);

  const lockPending = open && apiLockState === null;
  const lockedByOtherFromApi = apiLockState !== null ? Boolean(apiLockState.locked) : lockedByOther;
  const actionsDisabled = lockPending || lockedByOtherFromApi;
  const showLockIndicator = !lockPending && lockedByOtherFromApi;
  const dataLockedState = lockPending ? "pending" : String(lockedByOtherFromApi);

  const checksheetLocked = async () => {
    const {status, data} = await apiChecksheetLock.callGet(targetChecksheet.id);
    if (status !== 200 || !data) return false;
    if (isMounted()) setApiLockState(data);
    const {locked, user} = data;
    if (locked && user) addToast(`Locked by ${user.firstName} ${user.lastName}`);
    return locked || false;
  };

  const runIfUnlocked = async action => {
    const isLocked = await checksheetLocked();
    if (!isLocked) action();
  };

  const getLockTitle = defaultTitle => {
    if (lockPending) return "Checking lock status";
    if (lockedByOtherFromApi) {
      return lockHolder ? `Lock Holder: ${lockHolder}` : "Lock Holder";
    }
    return defaultTitle;
  };

  const lockMessage = lockHolder ? `Locked by ${lockHolder}` : "Locked by another user";

  const getNotesColumnContent = record => {
    if (record.overdue && !record.archived && atLeast("admin"))
      return (
        <Inline>
          {!!record.noteCount && (
            <Abbr title={`${record.noteCount} record note(s)`}>
              <NoteIcon
                onClick={() => {
                  loadRecordDetail(record.id).then(detail => {
                    if (detail) setModalNotesVisible(true);
                  });
                }}>
                <FontAwesomeIcon icon={faStickyNote} />
                <p>{record.noteCount}</p>
              </NoteIcon>
            </Abbr>
          )}
        </Inline>
      );

    if (record.noteCount || record.fileCount) {
      const displayedNoteCount =
        record.noteCount +
        (atLeast("admin") && record.archivedNotes ? record.archivedNotes.length : 0);

      return (
        !!record.noteCount && (
          <Abbr title={`${displayedNoteCount} record note(s)`}>
            <NoteIcon
              onClick={() => {
                loadRecordDetail(record.id).then(detail => {
                  if (detail) setModalNotesVisible(true);
                });
              }}>
              <FontAwesomeIcon icon={faStickyNote} />
              <p>{displayedNoteCount}</p>
            </NoteIcon>
          </Abbr>
        )
      );
    }

    return null;
  };

  const getAttachmentsColumnContent = record =>
    !!record.fileCount && (
      <Abbr title={`${record.fileCount} record attachment(s)`}>
        <AttachmentIcon
          onClick={() => {
            loadRecordDetail(record.id).then(detail => {
              if (detail) setModalAttachmentsVisible(true);
            });
          }}>
          <FontAwesomeIcon icon={faPaperclip} />
          <p>{record.fileCount}</p>
        </AttachmentIcon>
      </Abbr>
    );

  const getRestrictToUsers = () => {
    if (checksheet.restrictTo?.[0]?.publicId)
      return checksheet.restrictTo.map(({email}) => email).join(", ");

    if (roles)
      return checksheet.restrictTo
        ?.filter(({id}) => roles.filter(({id: roleId}) => id === roleId)[0]?.label)
        .map(({id}) => roles.filter(({id: roleId}) => id === roleId)[0].label)
        .join(", ");

    return "roles";
  };

  const applyLegacyDateScrollFromUrl = useCallback(() => {
    if (hasStructuredDeepLinkSearch(window.location.search)) return false;

    const parsed = parseChecksheetsDeepLink({
      search: window.location.search,
      hash: window.location.hash
    });

    if (parsed.checksheetId !== checksheet.id || parsed.recordId != null || !parsed.searchQuery) {
      return false;
    }

    setSearchType("date");
    reset({search: parsed.searchQuery});
    setQuery(parsed.searchQuery);
    setOpen(true);
    return true;
  }, [reset, checksheet.id]);

  // Legacy ?{date}#{checksheetId} when not driven by parent queryInfo.
  useEffect(() => {
    if (queryInfo || !initialLoad) return;
    applyLegacyDateScrollFromUrl();
  }, [initialLoad, queryInfo, applyLegacyDateScrollFromUrl]);

  useEffect(() => {
    if (!queryInfo) return;

    const {query: queryString, checksheetId: tableId, recordId} = queryInfo;
    if (tableId !== checksheet.id) return;

    if (records === null) {
      if (queryString) {
        setSearchType("date");
        reset({search: queryString});
        setQuery(queryString);
      }
      setOpen(true);
      return;
    }

    const table = document.getElementById(`c${tableId}`);
    if (table) {
      const y = table.getBoundingClientRect().top - 100;
      window.scrollBy({behavior: "smooth", top: y});
    }

    if (recordId && !queryInfo.skipRecordModal) {
      openRecordById(recordId).finally(() => setQueryInfo(undefined));
    } else {
      setQueryInfo(undefined);
    }
  }, [queryInfo, records, reset, setQueryInfo, openRecordById, checksheet.id]);

  const frequencyToUse = useMemo(() => {
    if (checksheet?.state && checksheet.stateActive) return checksheet.state.frequency;
    return checksheet?.frequency;
  }, [checksheet]);

  const availableBaseToUse = useMemo(() => {
    if (checksheet?.state && checksheet.stateActive) return checksheet.state.availableBase;
    return checksheet?.availableBase;
  }, [checksheet]);

  const displayName = useMemo(() => {
    if (checksheet?.stateActive && checksheet?.state?.name) return checksheet.state.name;
    return checksheet?.name;
  }, [checksheet]);

  const hasAvailabilityWindow = !!(availableBaseToUse && checksheet.availableDate);

  return (
    <StyledTableWrapper data-testid="tableRecord.wrapper">
      <TableMenuRow>
        <Inline>
          <ChecksheetName>
            <Abbr title={displayName}>{displayName?.toUpperCase()}</Abbr>
          </ChecksheetName>
          {state && (
            <Pill color={state?.color ? `#${state.color}` : undefined} quiet noBold>
              <Abbr title={state?.name || "DEFAULT"}>{state?.name || "DEFAULT"}</Abbr>
            </Pill>
          )}
          {frequencyToUse?.name && (
            <Frequency>
              <Abbr title={frequencyToUse.name.toUpperCase()}>
                {frequencyToUse.name.toUpperCase()}
              </Abbr>
            </Frequency>
          )}
          {checksheet.draft && <Draft data-testid="draftIcon">DRAFT</Draft>}
          {checksheet.isArchived && <Draft>ARCHIVED</Draft>}
          {checksheet.dateDue && !checksheet.draft && (
            <NextDue to={`/facilities/${slug}/tasks`}>
              <Abbr
                title={`${
                  hasAvailabilityWindow
                    ? `Next Available: ${prettyDateWithDayInUserTimezone(
                        checksheet.availableDate,
                        settings.timezone,
                        "ddd, MMM D"
                      )} - `
                    : ""
                }${!hasAvailabilityWindow ? "Next Due: " : ""}${
                  prettyDateWithDayInUserTimezone(checksheet.dateDue, settings.timezone) || ""
                }`}>
                {hasAvailabilityWindow
                  ? `Next Available: ${prettyDateWithDayInUserTimezone(
                      checksheet.availableDate,
                      settings.timezone,
                      "ddd, MMM D YYYY"
                    )} - `
                  : ""}
                {!hasAvailabilityWindow && "Next Due: "}
                {prettyDateWithDayInUserTimezone(checksheet.dateDue, settings.timezone)}
              </Abbr>
            </NextDue>
          )}
          {!!checksheet.overdueCount && (
            <RecordCount
              title={`${checksheet.overdueCount} overdue record${
                checksheet.overdueCount === 1 ? "" : "s"
              }`}>
              {checksheet.overdueCount}
            </RecordCount>
          )}
          {!!checksheet.draftCount && (
            <RecordCount
              $warning
              title={`${checksheet.draftCount} draft record${
                checksheet.draftCount === 1 ? "" : "s"
              }`}>
              {checksheet.draftCount}
            </RecordCount>
          )}
        </Inline>
        <Inline>
          <ButtonWrapper
            data-testid="tableRecord.expand"
            type="button"
            title={open ? "Collapse table" : "Expand table"}
            onClick={() => {
              setOpen(prev => {
                if (onOpen) onOpen(!prev, checksheet.id);
                return !prev;
              });
              setRecords(null);
            }}>
            <Arrow rotate={!open ? "180deg" : "270deg"} />
          </ButtonWrapper>
        </Inline>
      </TableMenuRow>

      {open && (
        <>
          <TableMenuRow>
            <StyledSearchWrapper>
              {searchType === "text" && (
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
              )}
              <FormProvider {...form}>
                <Search
                  name="search"
                  type={searchType}
                  placeholder="Search..."
                  {...register("search")}
                  onChange={e => handleSearch(e, checksheet.id)}
                />
              </FormProvider>
              <Spaced>
                <ToggleSwitch
                  iconLeft={faKeyboard}
                  iconRight={faCalendarDay}
                  textLeft="Text"
                  textRight="Date"
                  onChange={() => {
                    setSearchType(prev => (prev === "text" ? "date" : "text"));
                    reset({search: ""});
                  }}
                  options={["text", "date"]}
                  defaultSelected="text"
                />
              </Spaced>
            </StyledSearchWrapper>
            <ButtonBarWrapper>
              {showLockIndicator && (
                <LockIndicator
                  data-testid="tableRecord.lockIndicator"
                  aria-label={lockMessage}
                  title={lockMessage}>
                  <FontAwesomeIcon icon={faLock} aria-hidden />
                  {lockMessage}
                </LockIndicator>
              )}
              <ButtonBar>
                {targetChecksheet.draft &&
                  roleCanAccessResource("facility_checksheet", "update") &&
                  isActive && (
                    <TransitionButton
                      type="button"
                      onClick={() => publishDraft(targetChecksheet.id)}
                      data-testid="checksheets.publishDraft">
                      <Abbr title="Publish">
                        <FontAwesomeIcon icon={faShareFromSquare} />
                      </Abbr>
                    </TransitionButton>
                  )}

                <TransitionButton
                  type="button"
                  onClick={() => setModalFieldRestrictionsVisible(true)}>
                  <Abbr title="Checksheet Info">
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </Abbr>
                </TransitionButton>

                {!isMobile() && atLeast("admin") && (
                  <TransitionButton type="button" onClick={() => setModalReviewsVisible(true)}>
                    {checksheet.reviews?.length > 0 && (
                      <Badge
                        count={checksheet.reviews?.length}
                        title="Required reviews"
                        offset="14px"
                        color={heroTheme.warning}
                      />
                    )}
                    <Abbr title="Add or Update Required Reviews">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                    </Abbr>
                  </TransitionButton>
                )}

                <TransitionButton
                  type="button"
                  onClick={() =>
                    navigate(`/facilities/${slug}/checksheets/${checksheet.id}/preview`)
                  }
                  data-testid="checksheets.publishDraft">
                  <Abbr title="Preview">
                    <FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
                  </Abbr>
                </TransitionButton>

                {roleCanAccessResource("facility_checksheet_record", "export") &&
                  records?.length > 0 &&
                  resolvedChecksheet?.builder && (
                    <Export type="button" onClick={() => showExport(resolvedChecksheet)}>
                      <Abbr title="Export">
                        <FontAwesomeIcon icon={faFileExport} />
                      </Abbr>
                    </Export>
                  )}

                {roleCanAccessResource("facility_checksheet", "create") &&
                  !checksheet.isArchived &&
                  isActive && (
                    <Edit
                      type="button"
                      onClick={() => {
                        setModalCloneVisible(true);
                      }}>
                      <Abbr title="Clone">
                        <FontAwesomeIcon icon={faClone} />
                      </Abbr>
                    </Edit>
                  )}

                {roleCanAccessResource("facility_checksheet", "update") &&
                  roleCanAccessPage("checksheet_editor") &&
                  isActive && (
                    <Edit
                      data-testid="tableRecord.editChecksheet"
                      data-locked={dataLockedState}
                      type="button"
                      disabled={actionsDisabled}
                      onClick={() =>
                        runIfUnlocked(() =>
                          navigate(`/facilities/${slug}/checksheets/${targetChecksheet.id}/edit`)
                        )
                      }>
                      <Abbr title={getLockTitle("Edit")}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Abbr>
                    </Edit>
                  )}

                {isActive &&
                  (roleCanAccessResource("facility_checksheet", "delete") ||
                    roleCanAccessResource("facility_checksheet", "archive")) && (
                    <>
                      {roleCanAccessResource("facility_checksheet", "archive") && (
                        <Archive
                          data-locked={dataLockedState}
                          type="button"
                          disabled={actionsDisabled}
                          onClick={() =>
                            runIfUnlocked(() => {
                              if (!targetChecksheet.isArchived) archiveChecksheet(targetChecksheet);
                              else restoreChecksheet(targetChecksheet);
                            })
                          }>
                          {!targetChecksheet.isArchived ? (
                            <Abbr title={getLockTitle("Archive")}>
                              <FontAwesomeIcon icon={faArchive} />
                            </Abbr>
                          ) : (
                            <Abbr title={getLockTitle("Restore")}>
                              <FontAwesomeIcon icon={faRotateLeft} />
                            </Abbr>
                          )}
                        </Archive>
                      )}

                      {roleCanAccessResource("facility_checksheet", "delete") && (
                        <Delete
                          data-testid="tableRecord.deleteChecksheet"
                          data-locked={dataLockedState}
                          type="button"
                          disabled={actionsDisabled}
                          onClick={() => runIfUnlocked(() => showDelete(targetChecksheet))}>
                          <Abbr title={getLockTitle("Delete")}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Abbr>
                        </Delete>
                      )}
                    </>
                  )}
              </ButtonBar>
            </ButtonBarWrapper>
          </TableMenuRow>
          <BaseTable
            headings={{
              facilityChecksheetId: {header: "", disabled: true},
              submission: {header: "Record", disabled: true},
              noteCount: {
                header: (
                  <Abbr title="Notes">
                    <NoteColumnHeader icon={faNoteSticky} />
                  </Abbr>
                ),
                disabled: false
              },
              fileCount: {
                header: (
                  <Abbr title="Attachments">
                    <AttachmentColumnHeader icon={faPaperclip} />
                  </Abbr>
                ),
                disabled: false
              },
              ...(facilityStates?.length ? {state: {header: "State", disabled: true}} : {}),
              status: {header: "Status", disabled: false},
              dateDue: {header: checksheet?.frequency ? "Due" : "", disabled: false},
              completedAt: {header: "Date Completed", disabled: false}
            }}
            data={
              !records
                ? null
                : records.map(record => ({
                    submission: (
                      <Inline>
                        {roleCanAccessResource("facility_checksheet_record", "view") && (
                          <>
                            <Button
                              type="button"
                              onClick={() => {
                                setVisible(true);
                                setSelected(record);
                              }}
                              data-testid="tableRecord.preview">
                              View
                            </Button>
                            {roleCanAccessResource("facility_checksheet_record", "archive") &&
                              record.overdue &&
                              !record.archived &&
                              isActive && (
                                <Edit
                                  onClick={() => {
                                    setSelected(record);
                                    setModalAcknowledgeVisible(true);
                                  }}>
                                  <Abbr title="Archive">
                                    <FontAwesomeIcon icon={faArchive} />
                                  </Abbr>
                                </Edit>
                              )}
                          </>
                        )}
                      </Inline>
                    ),
                    noteCount: getNotesColumnContent(record),
                    fileCount: getAttachmentsColumnContent(record),
                    state: record.state?.state ? (
                      <Pill
                        color={
                          record.state.state.color ? `#${record.state.state.color}` : undefined
                        }>
                        {record.state.state.name}
                      </Pill>
                    ) : (
                      <Pill>DEFAULT</Pill>
                    ),
                    status: (
                      <Inline>
                        <Status status={record.status} archived={record.archived} quiet>
                          {record.archived && (
                            <Abbr title="Archived">
                              <FontAwesomeIcon icon={faBoxArchive} />
                              &nbsp;
                            </Abbr>
                          )}
                          {record.status}
                        </Status>
                        {record.draft ? <Text quiet>- {record.percentComplete}%</Text> : ""}
                      </Inline>
                    ),
                    dateDue: record.overdue ? (
                      <WarningMsg>
                        {prettyDateWithDayInUserTimezone(
                          record.dateDue,
                          settings.timezone,
                          "ddd, MMM D YYYY"
                        )}
                      </WarningMsg>
                    ) : (
                      prettyDateWithDayInUserTimezone(
                        record.dateDue,
                        settings.timezone,
                        "ddd, MMM D YYYY"
                      )
                    ),
                    completedAt: !record.overdue && (
                      <Completed late={dayjs(record.completedAt).isAfter(dayjs(record.dateDue))}>
                        {record.completedBy &&
                          record.completedBy.firstName &&
                          `${record.completedBy.firstName} ${record.completedBy.lastName} on `}
                        {prettyDateWithDayInUserTimezone(
                          record.completedAt,
                          settings.timezone,
                          "ddd, MMM D YYYY"
                        )}
                      </Completed>
                    )
                  }))
            }
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            loading={loadingRecords}
          />
          <TableFooter>
            <Inline>
              {total > 10 && limits.length > 0 && (
                <PageLimit>
                  <Dropdown
                    options={limits}
                    selection={limit}
                    setSelection={selection => {
                      setLimit(selection);
                      setCurrentPage(1);
                    }}
                  />
                </PageLimit>
              )}
              {total > 0 && (
                <Text quiet>
                  {limit <= 30 && pageTotal > 1 && limits.length > 0 && "per page, "}
                  <ModalViewLink
                    disabled={!total || total <= 10}
                    onClick={
                      !total || total <= 10
                        ? undefined
                        : () => handleViewAll(checksheet, getChecksheetRecords)
                    }>
                    {total} total
                  </ModalViewLink>
                </Text>
              )}
              {checksheet.external && (
                <External quiet>
                  External checksheet available to&nbsp;
                  <Button type="button" onClick={() => setModalExternalVisible(true)} inText quiet>
                    {checksheet.restrictTo?.[0]?.publicId ? "users" : "roles"}
                  </Button>
                  .
                </External>
              )}
            </Inline>
            {limit <= 30 && pageTotal > 1 && (
              <Pagination
                current={currentPage}
                setCurrent={setCurrentPage}
                pageTotal={pageTotal}
                updateData={() => getChecksheetRecords(currentPage)}
                loading={loadingRecords}
              />
            )}
          </TableFooter>
        </>
      )}

      {visible && (
        <ModalRecordView
          visible={visible}
          setVisible={setVisible}
          record={selected}
          checksheet={resolvedChecksheet}
          updateData={getChecksheetRecords}
          locks={locks}
        />
      )}

      {modalNotesVisible && (
        <ModalNotes
          visible={modalNotesVisible}
          setVisible={setModalNotesVisible}
          pendingNotes={selected.postedNotes}
          resolvedNotes={selected.resolvedNotes}
          noteFileCount={selected.noteFileCount}
          noteCt={selected.noteCount}
          fileCt={selected.fileCount}
          files={selected.files}
          archivedNotes={atLeast("admin") ? selected.archivedNotes : null}
          updateData={getChecksheetRecords}
          checksheetName={displayName}
          dateDue={selected.dateDue}
          builder={
            selected.state?.id
              ? resolvedChecksheet.state?.builder || resolvedChecksheet.builder
              : resolvedChecksheet.builder
          }
        />
      )}

      {modalAttachmentsVisible && (
        <ModalAttachments
          visible={modalAttachmentsVisible}
          setVisible={setModalAttachmentsVisible}
          builder={resolvedChecksheet.builder}
          files={selected.files}
          pendingNotes={selected.postedNotes}
          resolvedNotes={selected.resolvedNotes}
        />
      )}

      {modalAcknowledgeVisible && (
        <ArchiveRecord
          visible={modalAcknowledgeVisible}
          setVisible={setModalAcknowledgeVisible}
          updateData={getChecksheetRecords}
          record={selected}
          date={prettyDateWithDayInUserTimezone(
            selected?.dateDue,
            settings.timezone,
            "MMM D, YYYY"
          )}
          task={checksheet}
          taskType="checksheet"
        />
      )}

      {modalExternalVisible && (
        <Modal visible={modalExternalVisible} setVisible={setModalExternalVisible}>
          <HeadingMedium>External Checksheet</HeadingMedium>
          <Text>
            This is an external checksheet available to&nbsp;
            {getRestrictToUsers()}. It is only available for completion via a link provided by
            email.
          </Text>
        </Modal>
      )}

      {modalFieldRestrictionsVisible && (
        <ModalFieldRestrictions
          visible={modalFieldRestrictionsVisible}
          setVisible={setModalFieldRestrictionsVisible}
          checksheet={checksheet}
        />
      )}

      {modalReviewsVisible && (
        <ModalReviews
          visible={modalReviewsVisible}
          setVisible={setModalReviewsVisible}
          checksheet={checksheet}
        />
      )}

      {modalCloneVisible && (
        <ModalCloneChecksheet
          visible={modalCloneVisible}
          setVisible={setModalCloneVisible}
          confirm={handleClone}
          checksheetName={
            frequencyToUse?.name
              ? `${frequencyToUse.name.toUpperCase()} ${displayName?.toUpperCase()}`
              : displayName?.toUpperCase()
          }
        />
      )}
    </StyledTableWrapper>
  );
};

TableRecord.propTypes = {
  index: PropTypes.number.isRequired,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  recordsRefreshKey: PropTypes.number,
  publishDraft: PropTypes.func.isRequired,
  showExport: PropTypes.func.isRequired,
  showDelete: PropTypes.func.isRequired,
  archiveChecksheet: PropTypes.func.isRequired,
  restoreChecksheet: PropTypes.func.isRequired,
  locks: PropTypes.objectOf(PropTypes.any),
  handleViewAll: PropTypes.func.isRequired,
  queryInfo: PropTypes.objectOf(PropTypes.any),
  setQueryInfo: PropTypes.func.isRequired,
  refresh: PropTypes.func,
  triggerOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  handledOpen: PropTypes.objectOf(PropTypes.any)
};

// Style Overrides
const TableMenuRow = styled.div`
  ${flex("row", "nowrap", "space-between", "center")};
  width: 100%;
  padding: ${pad}px;

  ${Inline}:first-child {
    max-width: 90%;
    width: inherit;
  }
`;

const ChecksheetName = styled(Heading)`
  text-overflow: ellipsis;
  overflow-x: hidden;
  display: block;
  white-space: nowrap;
  max-width: 65%;

  ${bp(2)} {
    max-width: inherit;
  }
`;

const Frequency = styled(Small)`
  width: fit-content;
  min-width: min-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.primary};
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  white-space: nowrap;
  ${voice.quiet};
`;

const Draft = styled(Small)`
  width: fit-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.warning};
  ${voice.quiet};
`;

const NextDue = styled(Link)`
  color: ${({theme}) => theme.primary};
  display: none;

  ${bp(2)} {
    display: block;
    text-overflow: ellipsis;
    overflow-x: hidden;
    white-space: nowrap;
    width: min-content;
  }
`;

const RecordCount = styled(Small)`
  width: fit-content;
  min-width: min-content;
  padding: 0 ${pad / 2}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background: ${({theme, $warning}) => ($warning ? theme.warning : theme.error)};
  ${voice.quiet};
  line-height: normal;
`;

const Export = styled(Button)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  background: ${({theme}) => theme.secondary};
  color: ${({theme}) => theme.tertiary};
`;

const TransitionButton = styled(Button)`
  ${({disabled}) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const Edit = styled(TransitionButton)`
  display: none;

  ${bp(1)} {
    display: block;
  }
`;

const Archive = styled(TransitionButton)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.warning};
`;

const Delete = styled(TransitionButton)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.error};
`;

const PageLimit = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${pad}px;

  select {
    padding: ${pad / 4}px;
  }
`;

const WarningMsg = styled(Error)`
  ${voice.normal};
  color: ${({theme}) => theme.error};
`;

const Completed = styled(Error)`
  ${voice.normal};
  color: ${({late, theme}) => (late ? theme.error : "inherit")};
`;

const NoteColumnHeader = styled(FontAwesomeIcon)`
  fill: ${({theme}) => theme.tertiary};
`;

const NoteIcon = styled(Inline)`
  flex-wrap: nowrap;
  color: ${({theme}) => theme.primary};
  width: min-content;
  cursor: pointer;
  margin-bottom: 2px;
  font-size: 14px;
  gap: 2px;

  svg {
    pointer-events: none;
    fill: ${({theme}) => theme.primary};
    font-size: 14px;
  }

  p {
    margin-top: 1px;
    margin-left: ${pad / 2}px;
  }
`;

const AttachmentColumnHeader = styled(FontAwesomeIcon)`
  fill: ${({theme}) => theme.tertiary};
`;

const AttachmentIcon = styled(Inline)`
  flex-wrap: nowrap;
  color: ${({theme}) => theme.primary};
  width: min-content;
  cursor: pointer;
  margin-bottom: 2px;
  font-size: 14px;

  svg {
    pointer-events: none;
    fill: ${({theme}) => theme.primary};
    font-size: 14px;
  }

  p {
    margin-top: 1px;
    margin-left: ${pad / 2}px;
  }
`;

const Spaced = styled.div`
  height: min-content;
  margin-left: ${pad / 2}px;

  ${bp(2)} {
    margin-left: ${pad}px;
  }
`;

const Status = styled(Pill)`
  width: 118px;
  justify-content: center;

  background: ${({status, theme}) => {
    if (status === "Incomplete") return theme.warning;
    if (status === "Overdue") return theme.error;
    if (status === "Late") return theme.alert;
    return theme.success;
  }};

  ${({archived}) =>
    archived &&
    css`
      opacity: 0.7;
    `}

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

const StyledSearchWrapper = styled(SearchWrapper)`
  max-width: calc(100% - 176px);
`;

const External = styled(Text)`
  display: inherit;
`;

const ModalViewLink = styled.span`
  ${({disabled}) =>
    !disabled &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `}
`;

const StyledTableWrapper = styled(TableWrapper)`
  min-width: 330px;
`;

const ButtonBarWrapper = styled.div`
  position: relative;
  margin-left: ${pad / 2}px;
  flex-shrink: 0;
`;

const ButtonBar = styled(Inline)`
  gap: 7px;

  ${bp(1)} {
    gap: ${pad}px;
  }
`;

const LockIndicator = styled.span`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: ${pad / 4}px;
  ${voice.quiet};
  color: ${colors.heroBlack};
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  svg {
    flex-shrink: 0;
    fill: ${colors.heroBlack};
  }
`;

export default TableRecord;
